import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Button } from "./ui/button";
import { fetchSecondaryListings, buySecondaryListing } from "../lib/api";
import { getUser, saveUser } from "../lib/auth";
import { gbp } from "../lib/format";
import { Coins, ShoppingCart, Loader2, Trash2, User2 } from "lucide-react";
import SellFractionForm from "./SellFractionForm";

const priceChip = (variance) => {
  if (variance === 0) return { label: "Pari Passu", color: "#334155", bg: "#F1F5F9" };
  if (variance > 0) return { label: `+${variance}% Premium`, color: "#065F46", bg: "#D1FAE5" };
  return { label: `${variance}% Discount`, color: "#8B6F1D", bg: "rgba(212,175,55,0.14)" };
};

export const SecondaryListings = ({ onWalletChange, properties }) => {
  const [remoteListings, setRemoteListings] = useState([]);
  const [localListings, setLocalListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);

  const load = () => {
    setLoading(true);
    fetchSecondaryListings()
      .then(setRemoteListings)
      .catch(() => toast.error("Could not load secondary listings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleBuy = async (listing) => {
    const u = getUser();
    if (!u) return;
    setBuyingId(listing.id);
    try {
      const res = await buySecondaryListing(u.id, listing.id);
      saveUser({ ...u, wallet_gbp: res.wallet_gbp });
      toast.success(`Acquired ${listing.tokens.toLocaleString()} BSTK of ${listing.property_name} for ${gbp(listing.total_price_gbp)}.`);
      onWalletChange?.(res.wallet_gbp);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trade failed.");
    } finally {
      setBuyingId(null);
    }
  };

  const cancelLocal = (id) => {
    setLocalListings((prev) => prev.filter((l) => l.id !== id));
    toast.success("Listing withdrawn from notice board.");
  };

  const listings = [...localListings, ...remoteListings];

  return (
    <div className="space-y-8">
      <SellFractionForm
        properties={properties}
        onListingAdded={(l) => setLocalListings((prev) => [l, ...prev])}
      />

      <section className="rounded-lg border border-slate-200 bg-white" data-testid="secondary-listings">
        <div className="flex items-end justify-between border-b border-slate-100 px-6 py-5">
          <div>
            <div className="overline">Peer-to-Peer</div>
            <h2 className="mt-1 flex items-center gap-2 font-display text-2xl font-semibold tracking-tight text-slate-900">
              <Coins className="h-5 w-5 brickstok-gold" /> Secondary Market Notice Board
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Fractional positions listed for sale by Brickstok token holders. Instant stablecoin settlement.
            </p>
          </div>
          <div className="text-xs text-slate-500 font-mono-num" data-testid="secondary-count">
            {listings.length} open {listings.length === 1 ? "listing" : "listings"}
          </div>
        </div>

        {loading && listings.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /> Loading listings…
          </div>
        ) : listings.length === 0 ? (
          <div className="p-12 text-center" data-testid="secondary-empty">
            <div className="overline">No open listings</div>
            <h3 className="mt-2 font-display text-lg font-semibold text-slate-900">Market is quiet</h3>
            <p className="mt-2 text-sm text-slate-500">Check back shortly — or list one of your own positions above.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="pl-6 uppercase tracking-widest text-[11px] text-slate-500">Asset</TableHead>
                <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Fractions for Sale</TableHead>
                <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Discount / Premium</TableHead>
                <TableHead className="uppercase tracking-widest text-[11px] text-slate-500">Seller Wallet</TableHead>
                <TableHead className="text-right uppercase tracking-widest text-[11px] text-slate-500">Total Price</TableHead>
                <TableHead className="pr-6 text-right uppercase tracking-widest text-[11px] text-slate-500">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {listings.map((l) => {
                const chip = priceChip(l.price_variance_pct);
                const isLocal = !!l.is_local;
                return (
                  <TableRow key={l.id} className="hover:bg-slate-50/60" data-testid={`secondary-row-${l.id}`}>
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-slate-900">{l.property_name}</div>
                        {isLocal && (
                          <span
                            className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                            style={{ background: "rgba(212,175,55,0.14)", color: "#8B6F1D" }}
                            data-testid={`local-badge-${l.id}`}
                          >
                            <User2 className="h-2.5 w-2.5" /> Your listing
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{l.property_city} · {l.property_asset_type}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-900">
                      <div>{l.tokens.toLocaleString()} <span className="text-xs text-slate-500">BSTK</span></div>
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex rounded-sm px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest"
                        style={{ background: chip.bg, color: chip.color, border: `1px solid ${chip.bg}` }}
                      >
                        {chip.label}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="rounded-sm bg-slate-900 px-2 py-1 font-mono text-xs text-white">{l.seller_wallet}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-slate-900">
                      <div>{gbp(l.total_price_gbp)}</div>
                      <div className="text-xs text-slate-500">{gbp(l.price_per_token_gbp, { decimals: 3 })} / token</div>
                    </TableCell>
                    <TableCell className="pr-6 text-right">
                      {isLocal ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelLocal(l.id)}
                          className="border-slate-300 text-slate-700 hover:bg-slate-50"
                          data-testid={`cancel-listing-${l.id}`}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" /> Withdraw
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleBuy(l)}
                          disabled={buyingId === l.id}
                          className="text-slate-900 hover:brightness-95"
                          style={{ background: "#D4AF37" }}
                          data-testid={`buy-listing-${l.id}`}
                        >
                          {buyingId === l.id ? (
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ShoppingCart className="mr-2 h-3.5 w-3.5" />
                          )}
                          Buy via Stablecoin
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
};

export default SecondaryListings;
